<template>
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">
    <div class="bg-white dark:bg-slate-900 w-full max-w-4xl rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col max-h-[95vh]">

      <!-- Encabezado -->
      <div class="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/30">
        <div>
          <h2 class="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Users class="w-5 h-5 text-emerald-600" />
            {{ form.id ? 'Editar Usuario' : 'Nuevo Usuario' }}
          </h2>
          <p class="text-xs text-slate-500">Configuración de acceso, perfil y membresía.</p>
        </div>
        <button @click="$emit('close')" class="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full transition-colors text-slate-500">
          <X class="w-5 h-5" />
        </button>
      </div>

      <!-- Pestañas -->
      <div class="flex overflow-x-auto bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
        <button v-for="tab in tabs" :key="tab.id" @click="activeTab = tab.id"
          :class="['px-6 py-3 text-xs font-bold uppercase tracking-wider whitespace-nowrap transition-all border-b-2',
          activeTab === tab.id ? 'border-emerald-600 text-emerald-600 bg-white dark:bg-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700']">
          {{ tab.name }}
        </button>
      </div>

      <!-- Formulario -->
      <div class="flex-1 overflow-y-auto p-6 custom-scrollbar">
        <form id="tenantForm" @submit.prevent="handleSubmit" class="space-y-6" novalidate>

          <!-- PESTAÑA 1: DATOS PRINCIPALES -->
          <div v-show="activeTab === 'general'" class="space-y-6 animate-fade-in">
            <div :class="sectionCardClasses">
              <h3 :class="sectionTitleClasses">
                <Tag class="w-4 h-4 text-emerald-600" />
                Información Personal
              </h3>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label :class="labelClasses">* Nombre Completo</label>
                  <input v-model="form.full_name" type="text" :class="inputClasses" required />
                </div>
                <div>
                  <label :class="labelClasses">* Correo Electrónico</label>
                  <input v-model="form.email" type="email" :class="inputClasses" required />
                </div>
                <div>
                  <label :class="labelClasses">* Username</label>
                  <input v-model="form.username" type="text" :class="inputClasses" required />
                </div>
                <div>
                  <label :class="labelClasses">Documento (ID / RUT)</label>
                  <input v-model="form.document" type="text" :class="inputClasses" />
                </div>
              </div>
            </div>
          </div>

          <!-- PESTAÑA 2: CUENTA Y ACCESO -->
          <div v-show="activeTab === 'account'" class="space-y-6 animate-fade-in">
            <div :class="sectionCardClasses">
              <h3 :class="sectionTitleClasses">
                <Lock class="w-4 h-4 text-emerald-600" />
                Seguridad y Permisos
              </h3>

              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label :class="labelClasses">* Rol del Sistema</label>
                  <select v-model="form.role_id" :class="inputClasses" required>
                    <option :value="1">Administrador</option>
                    <option :value="3">Cliente</option>
                  </select>
                </div>
                <div>
                  <label :class="labelClasses">Estado de Cuenta</label>
                  <select v-model="form.is_active" :class="inputClasses">
                    <option :value="1">Activo</option>
                    <option :value="0">Inactivo / Suspendido</option>
                  </select>
                </div>
                <div>
                  <label :class="labelClasses">Suscripción Premium</label>
                  <select v-model="form.is_premium" :class="inputClasses">
                    <option :value="0">Plan Gratuito</option>
                    <option :value="1">Plan Basico</option>
                    <option :value="2">Plan Premium</option>
                  </select>
                </div>
              </div>

              <div class="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                <label :class="labelClasses">{{ form.id ? 'Nueva Contraseña (Opcional)' : '* Contraseña Inicial' }}</label>
                <input v-model="form.password_hash" type="password" :class="inputClasses" :placeholder="form.id ? 'Dejar en blanco para no cambiar' : 'Asignar contraseña'" :required="!form.id" />
              </div>
            </div>
          </div>

          <!-- PESTAÑA 3: PERFIL ADICIONAL -->
          <div v-show="activeTab === 'profile'" class="space-y-6 animate-fade-in">
            <div :class="sectionCardClasses">
              <h3 :class="sectionTitleClasses">
                <User class="w-4 h-4 text-emerald-600" />
                Datos Secundarios
              </h3>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label :class="labelClasses">Empresa Asociada (ID)</label>
                  <input v-model="form.company" type="number" :class="inputClasses" placeholder="Ej: 12" />
                </div>
                <div>
                  <label :class="labelClasses">Ocupación</label>
                  <input v-model="form.occupation" type="text" :class="inputClasses" />
                </div>
                <div>
                  <label :class="labelClasses">Fecha de Nacimiento</label>
                  <input v-model="form.birth_date" type="date" :class="inputClasses" />
                </div>
                <div>
                  <label :class="labelClasses">Género</label>
                  <select v-model="form.gender" :class="inputClasses">
                    <option :value="null">No especificado</option>
                    <option :value="1">Masculino</option>
                    <option :value="2">Femenino</option>
                  </select>
                </div>
                <div class="md:col-span-2">
                  <label :class="labelClasses">Biografía / Notas</label>
                  <textarea v-model="form.bio" rows="3" :class="inputClasses"></textarea>
                </div>
              </div>
            </div>
          </div>

        </form>
      </div>

      <!-- Pie del Modal -->
      <div class="px-6 py-4 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center z-20">
        <div class="flex gap-3 w-full sm:w-auto justify-end ml-auto">
          <button @click="$emit('close')" type="button" :class="btnSecondary">Cancelar</button>
          <button v-if="activeTab !== 'profile'" @click="nextTab" type="button" :class="btnPrimary">Siguiente</button>
          <button v-else type="submit" form="tenantForm" :disabled="isSaving" :class="btnPrimary">
            <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
            <Save v-else class="w-4 h-4" />
            Guardar
          </button>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, reactive, watch } from 'vue';
import { X, Save, Loader2, Users, Tag, Lock, User } from 'lucide-vue-next';
import { useToast } from '@/composables/useToast';
import apiClient from '@/api/client';

const props = defineProps({ show: Boolean, tenantToEdit: Object });
const emit = defineEmits(['close', 'saved']);
const { showSuccess, showError } = useToast();

const activeTab = ref('general');
const isSaving = ref(false);

const tabs = [
  { id: 'general', name: '1. Datos Principales' },
  { id: 'account', name: '2. Cuenta & Acceso' },
  { id: 'profile', name: '3. Perfil Adicional' }
];

const inputClasses = "w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all text-slate-700 dark:text-slate-200";
const labelClasses = "text-[10px] font-bold text-slate-500 uppercase mb-1 block";
const sectionCardClasses = "bg-slate-50/50 dark:bg-slate-800/30 p-5 rounded-xl border border-slate-100 dark:border-slate-800";
const sectionTitleClasses = "text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2 mb-4 uppercase tracking-tight";
const btnPrimary = "px-6 py-2.5 text-sm font-bold text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-70 w-full sm:w-auto";
const btnSecondary = "px-4 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 transition-colors w-full sm:w-auto";

const form = reactive({
  id: null,
  username: '',
  password_hash: '',
  document: '',
  email: '',
  role_id: 1, // Por defecto cliente
  full_name: '',
  is_active: 1,
  company: null,
  bio: '',
  occupation: '',
  birth_date: '',
  gender: null,
  is_premium: 0
});

const nextTab = () => {
  const currentIndex = tabs.findIndex(tab => tab.id === activeTab.value);
  if (currentIndex < tabs.length - 1) activeTab.value = tabs[currentIndex + 1].id;
};

watch(() => props.show, (val) => {
  if (val) {
    activeTab.value = 'general';
    if (props.tenantToEdit) {
      Object.assign(form, props.tenantToEdit);
      form.password_hash = ''; // Limpiamos password por seguridad
    } else {
      Object.assign(form, {
        id: null, username: '', password_hash: '', document: '', email: '',
        role_id: 1, full_name: '', is_active: 1, company: null, bio: '',
        occupation: '', birth_date: '', gender: null, is_premium: 0
      });
    }
  }
});

const handleSubmit = async () => {
  isSaving.value = true;
  try {
    const payload = { ...form };
    if (!form.id) delete payload.id;
    if (!payload.password_hash) delete payload.password_hash; // Si no escriben pass, no enviarla

    if (form.id) {
      await apiClient.put(`/users/${form.id}`, payload);
      showSuccess('Inquilino actualizado con éxito');
    } else {
      await apiClient.post('/users', payload);
      showSuccess('Inquilino creado correctamente');
    }

    emit('saved');
    emit('close');
  } catch (error) {
    showError('No se pudo guardar el inquilino.');
  } finally {
    isSaving.value = false;
  }
};
</script>

<style scoped>
@keyframes fade-in { from { opacity: 0; transform: scale(0.98); } to { opacity: 1; transform: scale(1); } }
.animate-fade-in { animation: fade-in 0.2s ease-out forwards; }
.custom-scrollbar::-webkit-scrollbar { width: 4px; }
.custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 20px; }
</style>